import React, { useMemo, useRef, useState, useEffect } from "react";
import ProjectCarousel from "../components/ProjectCarousel";
import HeroSection from "../components/HeroSection";
import { get } from "../api/config";

function StatCard({ title, value, footer, color }) {
  const colorClasses = useMemo(() => {
    switch (color) {
      case "blue":
        return {
          panel: "bg-white border-blue-500",
          title: "text-blue-600",
          value: "text-blue-700",
        };
      case "red":
        return {
          panel: "bg-white border-red-500",
          title: "text-red-600",
          value: "text-red-700",
        };
      case "orange":
        return {
          panel: "bg-white border-orange-500",
          title: "text-orange-600",
          value: "text-orange-700",
        };
      case "green":
        return {
          panel: "bg-white border-green-500",
          title: "text-green-600",
          value: "text-green-700",
        };
      default:
        return {
          panel: "bg-white",
          title: "text-gray-700",
          value: "text-gray-900",
        };
    }
  }, [color]);

  return (
    <div
      className={`border rounded-xl shadow-sm p-6 text-center ${colorClasses.panel}`}
    >
      <div className={`text-lg font-semibold ${colorClasses.title}`}>
        {title}
      </div>
      <div className={`mt-3 text-2xl font-bold ${colorClasses.value}`}>
        {value}
      </div>
      {footer ? (
        <div className="mt-4 text-xs text-gray-500">{footer}</div>
      ) : null}
    </div>
  );
}

function ImageStatCard({ image, title, value, footer }) {
  return (
    <div className="relative overflow-hidden rounded-lg">
      <img src={image} alt={title} className="w-full h-auto" />
      <div className="absolute inset-0" />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white">
        <div className="text-3xl font-extrabold leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
          {title}
        </div>
        <div className="mt-3 text-2xl font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
          {value}
        </div>
        {footer ? (
          <div className="mt-5 text-xs opacity-90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Home() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await get("public/statistics");
      setStats(response.data);
    } catch (err) {
      setError("Failed to load statistics");
      console.error("Error fetching statistics:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0";
    const numAmount = parseFloat(amount);

    if (numAmount >= 10000000) {
      return `₹ ${(numAmount / 10000000).toFixed(1)} Cr`;
    } else if (numAmount >= 100000) {
      return `₹ ${(numAmount / 100000).toFixed(1)} L`;
    } else if (numAmount >= 1000) {
      return `₹ ${(numAmount / 1000).toFixed(1)} K`;
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    return new Intl.NumberFormat("en-IN").format(parseFloat(num));
  };

  const getCurrentDateFooter = () => {
    const date = new Date();
    return `as of ${date.getDate()} • ${date.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    })}`;
  };

  const publicImage = (name) => `${process.env.PUBLIC_URL}/images/${name}`;

  if (loading) {
    return (
      <div className="">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center py-8">
            <div className="text-gray-600">Loading statistics...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={fetchStatistics}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero banner */}
      <HeroSection />

      <div className="mx-auto max-w-6xl px-4">
        {/* Top meta row */}
        <div className="flex items-center justify-between text-sm text-gray-600 py-3">
          <div className="flex items-center gap-2">
            {/* Add any meta information here */}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-xl font-semibold text-center mb-6 text-green-600">
          MP Funds Dashboard
        </h1>

        {/* Stats grid (2 cards per row) using provided images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageStatCard
            image={publicImage("Group 34.png")}
            title="Total Funds"
            value={formatCurrency(stats?.total_funds)}
            footer={getCurrentDateFooter()}
          />
          <ImageStatCard
            image={publicImage("Group 35.png")}
            title="Allocated Funds"
            value={formatCurrency(stats?.total_allocated)}
            footer={getCurrentDateFooter()}
          />
          <ImageStatCard
            image={publicImage("Group 36.png")}
            title="Utilized Funds"
            value={formatCurrency(stats?.total_utilized)}
            footer={getCurrentDateFooter()}
          />
          <ImageStatCard
            image={publicImage("Group 37.png")}
            title="Remaining Funds"
            value={formatCurrency(stats?.total_remaining)}
            footer={getCurrentDateFooter()}
          />
        </div>

        {/* Additional Statistics Cards */}
        {stats && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Allocations"
              value={stats.allocation_count || 0}
              color="blue"
              footer="Total fund allocations"
            />
            <StatCard
              title="Fund Sources"
              value={stats.fund_sources?.length || 0}
              color="green"
              footer="Different funding sources"
            />
            <StatCard
              title="Projects Funded"
              value={stats.utilization_by_project?.length || 0}
              color="orange"
              footer="Projects with fund allocation"
            />
            <StatCard
              title="Utilization Rate"
              value={`${(
                (parseFloat(stats.total_utilized) /
                  parseFloat(stats.total_allocated)) *
                  100 || 0
              ).toFixed(1)}%`}
              color="red"
              footer="Allocated vs Utilized"
            />
          </div>
        )}

        {/* Fund Sources Breakdown */}
        {stats?.fund_sources && stats.fund_sources.length > 0 && (
          <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Fund Sources
            </h3>
            <div className="space-y-3">
              {stats.fund_sources.map((source, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                >
                  <span className="font-medium text-gray-700">
                    {source.fund_source}
                  </span>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(source.total_amount)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {source.count} transaction{source.count !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Project Utilization */}
        {stats?.utilization_by_project &&
          stats.utilization_by_project.length > 0 && (
            <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Project Fund Utilization
              </h3>
              <div className="space-y-4">
                {stats.utilization_by_project.map((project, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">
                        {project.project_name}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          project.utilization_percentage === 0
                            ? "bg-red-100 text-red-800"
                            : project.utilization_percentage < 50
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {project.utilization_percentage}% Utilized
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Allocated</div>
                        <div className="font-semibold">
                          {formatCurrency(project.total_allocated)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600">Utilized</div>
                        <div className="font-semibold">
                          {formatCurrency(project.total_utilized)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Promo banner */}
        <div className="mt-8">
          <img
            src={`${process.env.PUBLIC_URL}/images/Rectangle 163.png`}
            alt="Promotional banner"
            className="w-full h-auto rounded-lg"
            loading="lazy"
          />
        </div>

        {/* Projects carousel */}
        <ProjectCarousel />
      </div>
    </div>
  );
}

export default Home;
